import { Trans } from '@lingui/react'
import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  makeStyles,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  useTheme
} from '@material-ui/core'
import { ThemeProvider } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  ApiError,
  TokenAndSignedDocuments,
  usePostAppointmentsDocumentsConsents,
  usePostTokenIntrospection
} from '../Api/chatbridgeApi'
import AppointmentEventLogData from '../Models/AppointmentEventLogData'
import SignatureModel from '../Models/SignatureModel'
import UserDataModel from '../Models/UserDataModel'
import defaultTheme from '../themes/default'
import validateAndExtractToken from '../Utils/JWTokenReader'
import useQuery from '../Utils/useQuery'
import AllowToContinue from './AllowToContinue'
import ChatRoom from './ChatRoom'
import HabeasData from './HabeasData'
import InformedConsent from './InformedConsent'
import NotValid from './NotValid'
import UserDataForm from './UserDataForm'

export interface StepperContainerProps {}

const useStyles = makeStyles(theme => ({
  page: {
    backgroundImage: 'url(images/login-design.jpg)',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    minHeight: '100vh',
    width: '100%',
    paddingTop: '20px'
  },
  page_full: {
    backgroundImage: 'url(images/login-design.jpg)',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    minHeight: '100vh',
    width: '100%',
    paddingLeft: '0px',
    paddingRight: '0px'
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  continueButton: {
    padding: theme.spacing(2)
  }
}))

const getSignature = (eventlog: AppointmentEventLogData, userData: UserDataModel, token: string) => {
  return {
    patientName: eventlog?.PatientName,
    patientId: userData.docNumber,
    asistingPersonName: userData.name,
    locationName: eventlog?.NombreEntidad + ' ' + eventlog?.NombreSede,
    examName: eventlog?.ExamName,
    specialistName: eventlog?.SpecialistName,
    appointmentDate: eventlog?.AppointmentDate,
    appointmentId: eventlog?.AppointmentId,
    JWT: token
  } as SignatureModel
}

const TooEarlyErrorCode = 4001
const TooLateErrorCode = 4002

const isAllowToContinue = (errorCode: number): boolean => {
  return errorCode === TooEarlyErrorCode || errorCode === TooLateErrorCode
}

const StepperContainer: FunctionComponent<StepperContainerProps> = () => {
  const query = useQuery()
  const token = query.get('token') ?? ''
  const chatRoom = query.get('chatroom') ?? ''

  const classes = useStyles()
  const [userData, setUserData] = useState({ name: '', docNumber: '' } as UserDataModel)
  const [activeStep, setActiveStep] = useState(0)
  const [areDocsSent, setAreDocsSent] = useState(false)
  const [enterChatRoom, setEnterChatRoom] = useState(false)
  const [isValidToken, setIsValidToken] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [errorCode, setErrorCode] = useState(0)
  const [eventlog, setEventlog] = useState(null as AppointmentEventLogData | null)

  const postTokenIntrospection = usePostTokenIntrospection({})
  const postAppointmentsDocuments = usePostAppointmentsDocumentsConsents({ appointment_id: chatRoom })

  const theme = useTheme()
  const isScreenXS = useMediaQuery(theme.breakpoints.down('xs'))

  useEffect(() => {
    if (token === '' || chatRoom === '') {
      setIsValidToken(false)
      return
    }
    const eventlogOut = validateAndExtractToken(token)
    if (eventlogOut === null) {
      setIsValidToken(false)
      return
    }
    setEventlog(eventlogOut)
    postTokenIntrospection
      .mutate({ token })
      .then((res: any) => {
        setIsValidToken(true)
        if (eventlogOut?.RolinCall === 'Specialist') setEnterChatRoom(true)
      })
      .catch(value => {
        const apiError = value.data as ApiError

        // The specialist will have preferential treatment and may continue to the chat room at any time
        // while the appointment is active.
        if (isAllowToContinue(apiError.error_code) && eventlogOut?.RolinCall === 'Specialist') {
          setIsValidToken(true)
          setEnterChatRoom(true)
          return
        }

        setErrorCode(apiError.error_code)
        setErrorMessage(apiError.message_error)
        setIsValidToken(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (areDocsSent) {
      postAppointmentsDocuments
        .mutate({
          token,
          name: userData.name,
          document_number: userData.docNumber,
          signed_documents: ['TermsAndConditions', 'InformedConsent']
        } as TokenAndSignedDocuments)
        .then(res => {
          setEnterChatRoom(true)
        })
        .catch(value => {
          setErrorMessage((value.data as ApiError).message_error)
          setIsValidToken(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areDocsSent])

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleContinue = () => {
    setIsValidToken(true)
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      {enterChatRoom ? (
        <ChatRoom chatRoom={chatRoom} eventLog={eventlog as AppointmentEventLogData} />
      ) : (
        <div className={isScreenXS ? classes.page_full : classes.page}>
          <Container maxWidth='md' disableGutters={isScreenXS} id='telemedicinaHeader'>
            <Paper elevation={3} style={{ padding: '3px' }}>
              {isValidToken ? (
                <Stepper activeStep={activeStep} orientation='vertical'>
                  <Step key='userDataForm'>
                    <StepLabel>
                      <Trans id='' message='Datos personales' />
                    </StepLabel>
                    <StepContent>
                      <UserDataForm userDataState={{ userData, setUserData }} />
                      <Button
                        id='nextBtn'
                        variant='contained'
                        color='primary'
                        onClick={handleNext}
                        disabled={userData.name === '' || userData.docNumber === ''}
                        style={{ marginLeft: '20px' }}
                      >
                        <Trans id='' message='Siguiente' />
                      </Button>
                    </StepContent>
                  </Step>
                  <Step key='InformedConsent'>
                    <StepLabel>
                      <Trans id='' message='Consentimiento Informado' />
                    </StepLabel>
                    <StepContent>
                      <InformedConsent
                        signatureData={getSignature(eventlog as AppointmentEventLogData, userData, token)}
                      />
                      <Button id='nextBtnInformed' variant='contained' color='primary' onClick={handleNext}>
                        <Trans id='' message='He leído y acepto' />
                      </Button>
                      &nbsp;
                      <Button id='backBtnInformed' variant='contained' color='default' onClick={handleBack}>
                        <Trans id='' message='Atrás' />
                      </Button>
                    </StepContent>
                  </Step>
                  <Step key='TermsAndConditions'>
                    <StepLabel>
                      <Trans id='' message='Términos y condiciones' />
                    </StepLabel>
                    <StepContent>
                      <HabeasData signatureData={getSignature(eventlog as AppointmentEventLogData, userData, token)} />
                      <Button id='nextBtnHabeas' variant='contained' color='primary' onClick={handleNext}>
                        <Trans id='' message='He leído y acepto' />
                      </Button>
                      &nbsp;
                      <Button variant='contained' color='default' onClick={handleBack}>
                        <Trans id='' message='Atrás' />
                      </Button>
                    </StepContent>
                  </Step>
                  <Step key='Chatroom'>
                    <StepLabel>
                      <Trans id='' message='Entrar en la sala' />
                    </StepLabel>
                    <StepContent>
                      <p>
                        <Trans id='' message='¡Muy bien! Todo está listo para entrar en la sala.' />
                      </p>
                      <Button
                        id='goToChatBtn'
                        variant='contained'
                        color='primary'
                        onClick={() => {
                          setAreDocsSent(true)
                        }}
                      >
                        <Trans id='' message='Entrar en la sala' />
                      </Button>
                      &nbsp;
                      <Button variant='contained' color='default' onClick={handleBack}>
                        <Trans id='' message='Atrás' />
                      </Button>
                    </StepContent>
                  </Step>
                </Stepper>
              ) : isAllowToContinue(errorCode) ? (
                <AllowToContinue message={errorMessage}>
                  {errorMessage && errorMessage !== '' && (
                    <div className={classes.continueButton}>
                      <Button variant='contained' color='primary' onClick={handleContinue}>
                        <Trans id='' message='Continuar' />
                      </Button>
                    </div>
                  )}
                </AllowToContinue>
              ) : (
                <NotValid message={errorMessage} />
              )}
            </Paper>
          </Container>
        </div>
      )}
      <Backdrop className={classes.backdrop} open={postTokenIntrospection.loading || postAppointmentsDocuments.loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </ThemeProvider>
  )
}

export default StepperContainer
